// GUIA DE LEITURA — Navbar.js
// Barra de navegação: gere menus, sessão do utilizador e mudanças de página.
// Comentários explicativos adicionados para estudo; a lógica original foi mantida.
// Conceitos: props = dados recebidos; state = dados que mudam; hook = função React reutilizável.

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { useEpocasContext } from '../context/EpocasContext';
import LoginModal from './LoginModal';
import {
  FaChevronDown,
  FaUserCircle,
  FaBars,
  FaTimes,
  FaHome,
  FaFutbol,
  FaUsers,
  FaChartBar,
  FaShieldAlt,
  FaUserFriends,
  FaSignOutAlt,
  FaCalendarAlt,
  FaTrophy,
  FaUser,
  FaHistory,
  FaImage,
  FaIdCard,
} from 'react-icons/fa';

// Define um componente ou função auxiliar. Os parâmetros entre parênteses são os dados que recebe.
function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();

  // Este hook/contexto disponibiliza dados e funções partilhados com outras partes da aplicação.
  const { logado, logout, user, isAdmin, isJogador, isEquipaTecnica } = useAuthContext();
  const {
    epocas,
    epocaSelecionada,
    setEpocaSelecionada,
  } = useEpocasContext();

  // useState guarda um valor entre renderizações; a função set... atualiza esse valor e o React redesenha o componente.
  const [modalAberto, setModalAberto] = useState(false);
  // useState guarda um valor entre renderizações; a função set... atualiza esse valor e o React redesenha o componente.
  const [submenuAberto, setSubmenuAberto] = useState(null);
  // useState guarda um valor entre renderizações; a função set... atualiza esse valor e o React redesenha o componente.
  const [hoveredLink, setHoveredLink] = useState(null);
  // useState guarda um valor entre renderizações; a função set... atualiza esse valor e o React redesenha o componente.
  const [mobileMenu, setMobileMenu] = useState(false);
  // useState guarda um valor entre renderizações; a função set... atualiza esse valor e o React redesenha o componente.
  const [scrolled, setScrolled] = useState(false);

  // useRef guarda uma referência persistente (por exemplo, um elemento HTML) sem causar nova renderização.
  const navRef = useRef(null);


  // Função auxiliar/ação: concentra a lógica que é chamada por um evento ou por outra função.
  const abrirModal = () => setModalAberto(true);
  // Função auxiliar/ação: concentra a lógica que é chamada por um evento ou por outra função.
  const fecharModal = () => setModalAberto(false);

  // Função auxiliar/ação: concentra a lógica que é chamada por um evento ou por outra função.
  const fecharTudo = () => {
    setMobileMenu(false);
    setSubmenuAberto(null);
    setHoveredLink(null);
  };

  // Função auxiliar/ação: concentra a lógica que é chamada por um evento ou por outra função.
  const handleNavigate = (fn) => {
    fn();
    fecharTudo();
  };

  // Função auxiliar/ação: concentra a lógica que é chamada por um evento ou por outra função.
  const handleLogout = () => {
    logout();
    fecharTudo();
    navigate('/');
  };

  // useEffect executa um efeito quando as dependências mudam; o return interno pode limpar eventos ou temporizadores.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // useEffect executa um efeito quando as dependências mudam; o return interno pode limpar eventos ou temporizadores.
  useEffect(() => {
    fecharTudo();
  }, [location.pathname]);

  // useEffect executa um efeito quando as dependências mudam; o return interno pode limpar eventos ou temporizadores.
  useEffect(() => {
    const outside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setMobileMenu(false);
        setSubmenuAberto(null);
        setHoveredLink(null);
      }
    };

    document.addEventListener('mousedown', outside);

    return () => document.removeEventListener('mousedown', outside);
  }, []);

  const links = [
    {
      label: 'Início',
      icon: <FaHome />,
      path: '/',
      onClick: () => navigate('/'),
    },
    {
      label: 'Jogos',
      icon: <FaFutbol />,
      submenu: [
        {
          label: 'Calendário',
          icon: <FaCalendarAlt />,
          path: '/jogos',
          onClick: () => navigate('/jogos'),
        },
        {
          label: 'Classificação',
          icon: <FaTrophy />,
          external: true,
          onClick: () =>
            window.open(
              'https://domingoasdez.com/competicoes/2026-27/2a-divisao',
              '_blank',
              'noopener,noreferrer'
            ),
        },
      ],
    },
    {
      label: 'Equipa',
      icon: <FaUsers />,
      submenu: [
        {
          label: 'Plantel',
          icon: <FaUsers />,
          path: '/plantel',
          onClick: () => navigate('/plantel'),
        },
        {
          label: 'Equipa Técnica',
          icon: <FaUserFriends />,
          path: '/equipa-tecnica',
          onClick: () => navigate('/equipa-tecnica'),
        },
        {
          label: 'Direção',
          icon: <FaIdCard />,
          path: '/direcao',
          onClick: () => navigate('/direcao'),
        },
      ],
    },
    ...(logado && (isJogador || isAdmin || isEquipaTecnica)
      ? [
          {
            label: 'Estatísticas',
            icon: <FaChartBar />,
            submenu: [
              {
                label: 'Jogadores',
                icon: <FaUser />,
                path: '/estatisticas-jogador',
                onClick: () => navigate('/estatisticas-jogador'),
              },
              {
                label: 'Equipa',
                icon: <FaChartBar />,
                path: '/estatisticas-equipa',
                onClick: () => navigate('/estatisticas-equipa'),
              },
            ],
          },
        ]
      : []),
    {
      label: 'Clube',
      icon: <FaShieldAlt />,
      submenu: [
        {
          label: 'História',
          icon: <FaHistory />,
          path: '/historia-clube',
          onClick: () => navigate('/historia-clube'),
        },
        {
          label: 'Plantéis Anos Anteriores',
          icon: <FaUsers />,
          path: '/planteis-anteriores',
          onClick: () => navigate('/planteis-anteriores'),
        },
        {
          label: 'Patrocinadores',
          icon: <FaImage />,
          path: '/patrocinadores',
          onClick: () => navigate('/patrocinadores'),
        },
      ],
    },
    ...(logado && isAdmin
      ? [
          {
            label: 'Sócios',
            icon: <FaIdCard />,
            path: '/socios',
            onClick: () => navigate('/socios'),
          },
        ]
      : []),
    logado
      ? {
          label: user?.displayName || user?.email || 'A minha conta',
          icon: <FaUserCircle />,
          accountMenu: true,
          submenu: [
            ...(isJogador
              ? [
                  {
                    label: 'Os meus dados',
                    icon: <FaUser />,
                    path: '/meus-dados',
                    onClick: () => navigate('/meus-dados'),
                  },
                ]
              : []),
            {
              label: 'Logout',
              icon: <FaSignOutAlt />,
              danger: true,
              onClick: handleLogout,
            },
          ],
        }
      : {
          label: 'Login',
          icon: <FaUserCircle />,
          onClick: abrirModal,
        },
  ];

  const isLinkActive = (link) => {
    if (link.path) {
      return location.pathname === link.path;
    }

    if (link.submenu) {
      return link.submenu.some(
        (item) => item.path && location.pathname === item.path
      );
    }

    return false;
  };

  const renderLinks = (isMobile = false) =>
    links.map((link) => {
      const ativo = isLinkActive(link);
      const submenuVisivel =
        submenuAberto === link.label ||
        (!isMobile && hoveredLink === link.label);

      return (
        <div
          key={link.label}
          className={[
            'gd-nav-item',
            ativo ? 'gd-nav-item-active' : '',
            submenuVisivel ? 'gd-nav-item-open' : '',
            link.accountMenu ? 'gd-nav-account' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onMouseEnter={() => {
            if (!isMobile && link.submenu) {
              setHoveredLink(link.label);
            }
          }}
          onMouseLeave={() => {
            if (!isMobile && link.submenu) {
              setHoveredLink(null);
            }
          }}
        >
          <button
            type="button"
            className="gd-nav-link"
            onClick={() => {
              if (link.submenu) {
                if (isMobile) {
                  setSubmenuAberto(
                    submenuAberto === link.label ? null : link.label
                  );
                } else {
                  setSubmenuAberto(
                    submenuAberto === link.label ? null : link.label
                  );
                }
              } else if (link.onClick) {
                handleNavigate(link.onClick);
              }
            }}
            aria-expanded={
              link.submenu ? Boolean(submenuVisivel) : undefined
            }
          >
            <span className="gd-nav-link-icon">{link.icon}</span>
            <span className="gd-nav-link-label">{link.label}</span>

            {link.submenu && (
              <FaChevronDown
                className={`gd-nav-chevron ${
                  submenuVisivel ? 'gd-nav-chevron-open' : ''
                }`}
              />
            )}
          </button>

          {link.submenu && submenuVisivel && (
            <div className="gd-nav-dropdown">
              {link.submenu.map((item) => (
                <button
                  type="button"
                  key={item.label}
                  className={[
                    'gd-nav-dropdown-item',
                    item.path && location.pathname === item.path
                      ? 'gd-nav-dropdown-active'
                      : '',
                    item.danger ? 'gd-nav-dropdown-danger' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handleNavigate(item.onClick)}
                >
                  <span className="gd-nav-dropdown-icon">
                    {item.icon}
                  </span>
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    });

  const epocasOrdenadas = [...(epocas || [])].sort((a, b) => {
    const anoA = parseInt(a.split('/')[0], 10);
    const anoB = parseInt(b.split('/')[0], 10);
    return anoB - anoA;
  });

  const renderSeasonSelect = (mobile = false) => (
    <div
      className={`gd-season-wrapper ${
        mobile ? 'gd-season-mobile' : 'gd-season-desktop'
      }`}
    >
      <span className="gd-season-label">Época</span>
      <select
        aria-label="Selecionar época"
        value={epocaSelecionada || ''}
        onChange={(event) => setEpocaSelecionada(event.target.value)}
        className="gd-season-select"
      >
        {epocasOrdenadas.map((epoca) => (
          <option key={epoca} value={epoca}>
            {epoca}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <>
      <header
        className={`gd-navbar ${scrolled ? 'gd-navbar-scrolled' : ''}`}
        ref={navRef}
      >
        <div className="gd-navbar-inner">
          <button
            type="button"
            className="gd-brand"
            onClick={() => handleNavigate(() => navigate('/'))}
            aria-label="Ir para a página inicial"
          >
            <img
              src={`${process.env.PUBLIC_URL}/GDFeitos.png`}
              alt="Logótipo do Grupo Desportivo dos Feitos"
              className="gd-brand-logo"
            />
            <span className="gd-brand-text">
              <span className="gd-brand-name">FEITOS</span>
              <span className="gd-brand-subtitle">
                GRUPO DESPORTIVO
              </span>
            </span>
          </button>

          <nav className="gd-desktop-navigation" aria-label="Navegação principal">
            {renderLinks()}
          </nav>

          {renderSeasonSelect()}

          <button
            type="button"
            className="gd-mobile-toggle"
            onClick={() => {
              setMobileMenu(!mobileMenu);
              setSubmenuAberto(null);
            }}
            aria-label={mobileMenu ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={mobileMenu}
          >
            {mobileMenu ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        {mobileMenu && (
          <div className="gd-mobile-panel">
            <nav
              className="gd-mobile-navigation"
              aria-label="Navegação mobile"
            >
              {renderLinks(true)}
            </nav>

            {renderSeasonSelect(true)}
          </div>
        )}
      </header>

      {modalAberto && (
        <LoginModal
          onClose={fecharModal}
          onLoginSucesso={fecharModal}
        />
      )}

      <style>{`
        .gd-navbar {
          position: sticky;
          top: 0;
          z-index: 1000;
          width: 100%;
          color: #ffffff;
          background: rgba(24, 26, 29, 0.78);
          border-bottom: 1px solid rgba(255, 255, 255, 0.11);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.10);
          transition:
            background 0.25s ease,
            box-shadow 0.25s ease,
            border-color 0.25s ease;
        }

        .gd-navbar-scrolled {
          background: rgba(19, 21, 24, 0.94);
          border-bottom-color: rgba(255, 255, 255, 0.15);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
        }

        .gd-navbar-inner {
          width: 100%;
          max-width: none;
          min-height: 82px;
          margin: 0 auto;
          box-sizing: border-box;
          padding: 0 30px;
          display: flex;
          align-items: center;
          gap: 22px;
        }

        .gd-brand {
          display: inline-flex;
          align-items: center;
          flex-shrink: 0;
          gap: 12px;
          padding: 0;
          border: 0;
          color: #ffffff;
          background: transparent;
          cursor: pointer;
          text-align: left;
        }

        .gd-brand-logo {
          display: block;
          width: 39px;
          height: 48px;
          object-fit: contain;
          filter: drop-shadow(0 3px 8px rgba(0, 0, 0, 0.25));
          transition: transform 0.2s ease;
        }

        .gd-brand:hover .gd-brand-logo {
          transform: scale(1.04);
        }

        .gd-brand-text {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .gd-brand-name {
          font-size: 19px;
          line-height: 1;
          font-weight: 800;
          letter-spacing: 1.7px;
          white-space: nowrap;
        }

        .gd-brand-subtitle {
          color: #aeb1b6;
          font-size: 8px;
          font-weight: 600;
          letter-spacing: 2px;
          white-space: nowrap;
        }

        .gd-desktop-navigation {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 3px;
          margin-left: auto;
        }

        .gd-nav-item {
          position: relative;
          display: inline-flex;
          align-items: center;
        }

        .gd-nav-link {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-height: 42px;
          padding: 0 13px;
          border: 1px solid transparent;
          border-radius: 10px;
          color: #e0e1e3;
          background: transparent;
          font-size: 13px;
          font-weight: 500;
          white-space: nowrap;
          cursor: pointer;
          transition:
            color 0.2s ease,
            background 0.2s ease,
            border-color 0.2s ease;
        }

        .gd-nav-link:hover,
        .gd-nav-item-open > .gd-nav-link {
          color: #ffffff;
          background: rgba(255, 255, 255, 0.08);
        }

        .gd-nav-item-active > .gd-nav-link {
          color: #ffffff;
          background: rgba(255, 255, 255, 0.10);
          border-color: rgba(255, 255, 255, 0.12);
        }

        .gd-nav-item-active > .gd-nav-link::after {
          content: '';
          position: absolute;
          left: 13px;
          right: 13px;
          bottom: 4px;
          height: 2px;
          border-radius: 2px;
          background: #ffffff;
          opacity: 0.85;
        }

        .gd-nav-link-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          opacity: 0.9;
        }

        .gd-nav-chevron {
          margin-left: 1px;
          font-size: 9px;
          opacity: 0.65;
          transition: transform 0.2s ease;
        }

        .gd-nav-chevron-open {
          transform: rotate(180deg);
        }

        .gd-nav-dropdown {
          position: absolute;
          top: calc(100% + 10px);
          left: 50%;
          min-width: 220px;
          padding: 7px;
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 13px;
          background: rgba(28, 30, 34, 0.97);
          box-shadow: 0 18px 42px rgba(0, 0, 0, 0.35);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          transform: translateX(-50%);
          animation: gd-dropdown-in 0.16s ease-out;
        }

        .gd-nav-dropdown-item {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 11px;
          padding: 11px 12px;
          border: 0;
          border-radius: 8px;
          color: #d8d9dc;
          background: transparent;
          font-size: 12px;
          text-align: left;
          cursor: pointer;
          transition:
            color 0.18s ease,
            background 0.18s ease;
        }

        .gd-nav-dropdown-item:hover,
        .gd-nav-dropdown-active {
          color: #ffffff;
          background: rgba(255, 255, 255, 0.09);
        }

        .gd-nav-dropdown-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 16px;
          color: #aeb1b6;
          font-size: 12px;
          flex-shrink: 0;
        }

        .gd-nav-dropdown-danger {
          color: #e6e6e6;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 0 0 8px 8px;
          margin-top: 4px;
          padding-top: 13px;
        }

        .gd-nav-dropdown-danger:hover {
          color: #ffffff;
          background: rgba(255, 255, 255, 0.12);
        }

        .gd-season-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }

        .gd-season-label {
          color: #aeb1b6;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .gd-season-select {
          min-width: 116px;
          max-width: 160px;
          padding: 10px 28px 10px 11px;
          border: 1px solid rgba(255, 255, 255, 0.17);
          border-radius: 9px;
          outline: none;
          color: #ffffff;
          background-color: rgba(255, 255, 255, 0.06);
          font-size: 12px;
          cursor: pointer;
          transition:
            background 0.2s ease,
            border-color 0.2s ease;
        }

        .gd-season-select:hover,
        .gd-season-select:focus {
          border-color: rgba(255, 255, 255, 0.35);
          background-color: rgba(255, 255, 255, 0.10);
        }

        .gd-season-select option {
          color: #ffffff;
          background: #24272b;
        }

        .gd-mobile-toggle,
        .gd-mobile-panel,
        .gd-season-mobile {
          display: none;
        }

        @keyframes gd-dropdown-in {
          from {
            opacity: 0;
            margin-top: -4px;
          }
          to {
            opacity: 1;
            margin-top: 0;
          }
        }

        @media (max-width: 1450px) and (min-width: 1181px) {
          .gd-navbar-inner {
            padding: 0 20px;
            gap: 12px;
          }

          .gd-brand {
            gap: 9px;
          }

          .gd-brand-logo {
            width: 34px;
            height: 43px;
          }

          .gd-brand-name {
            font-size: 17px;
          }

          .gd-brand-subtitle {
            font-size: 7px;
          }

          .gd-desktop-navigation {
            gap: 0;
          }

          .gd-nav-link {
            gap: 6px;
            padding: 0 9px;
            font-size: 12px;
          }

          .gd-season-wrapper {
            gap: 5px;
          }

          .gd-season-label {
            display: none;
          }

          .gd-season-select {
            min-width: 104px;
            padding-left: 8px;
          }
        }

        @media (min-width: 1181px) {
          .gd-mobile-panel {
            display: none !important;
          }
        }

        @media (max-width: 1180px) {
          .gd-navbar-inner {
            min-height: 74px;
            padding: 0 22px;
          }

          .gd-desktop-navigation,
          .gd-season-desktop {
            display: none;
          }

          .gd-mobile-toggle {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 43px;
            height: 43px;
            margin-left: auto;
            border: 1px solid rgba(255, 255, 255, 0.16);
            border-radius: 11px;
            color: #ffffff;
            background: rgba(255, 255, 255, 0.06);
            font-size: 19px;
            cursor: pointer;
            transition: background 0.2s ease;
          }

          .gd-mobile-toggle:hover {
            background: rgba(255, 255, 255, 0.13);
          }

          .gd-mobile-panel {
            display: block;
            padding: 8px 20px 20px;
            border-top: 1px solid rgba(255, 255, 255, 0.08);
            background: rgba(20, 22, 25, 0.97);
            box-shadow: 0 16px 32px rgba(0, 0, 0, 0.2);
            animation: gd-dropdown-in 0.18s ease-out;
          }

          .gd-mobile-navigation {
            display: flex;
            flex-direction: column;
            gap: 3px;
          }

          .gd-mobile-navigation .gd-nav-item {
            display: flex;
            flex-direction: column;
            align-items: stretch;
            width: 100%;
          }

          .gd-mobile-navigation .gd-nav-link {
            justify-content: flex-start;
            width: 100%;
            min-height: 47px;
            padding: 0 14px;
            font-size: 14px;
            text-align: left;
          }

          .gd-mobile-navigation .gd-nav-link-icon {
            width: 20px;
            font-size: 15px;
          }

          .gd-mobile-navigation .gd-nav-chevron {
            margin-left: auto;
          }

          .gd-mobile-navigation .gd-nav-item-active > .gd-nav-link::after {
            left: 14px;
            right: auto;
            bottom: 8px;
            width: 22px;
          }

          .gd-mobile-navigation .gd-nav-dropdown {
            position: static;
            min-width: 0;
            width: 100%;
            padding: 4px 6px 8px 18px;
            border: 0;
            border-radius: 0;
            background: transparent;
            box-shadow: none;
            transform: none;
            animation: none;
          }

          .gd-mobile-navigation .gd-nav-dropdown-item {
            min-height: 42px;
            padding: 10px 13px;
            font-size: 13px;
          }

          .gd-season-mobile {
            display: flex;
            justify-content: space-between;
            margin-top: 15px;
            padding-top: 16px;
            border-top: 1px solid rgba(255, 255, 255, 0.10);
          }

          .gd-season-mobile .gd-season-label {
            display: inline;
            font-size: 11px;
          }

          .gd-season-mobile .gd-season-select {
            min-width: 145px;
          }
        }

        @media (max-width: 480px) {
          .gd-navbar-inner {
            min-height: 68px;
            padding: 0 15px;
          }

          .gd-brand {
            gap: 9px;
          }

          .gd-brand-logo {
            width: 32px;
            height: 40px;
          }

          .gd-brand-name {
            font-size: 16px;
            letter-spacing: 1.4px;
          }

          .gd-brand-subtitle {
            font-size: 7px;
            letter-spacing: 1.4px;
          }

          .gd-mobile-toggle {
            width: 40px;
            height: 40px;
          }

          .gd-mobile-panel {
            padding: 8px 13px 17px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .gd-navbar *,
          .gd-navbar *::before,
          .gd-navbar *::after {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </>
  );
}

export default Navbar;
